var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { finalizeEvent, generateSecretKey, getPublicKey, nip19, nip44, } from "nostr-tools";
import { bytesToHex } from "@noble/hashes/utils.js";
import { fetchFormTemplate, getDefaultRelays } from "./utils/fetchFormTemplate.js";
import { stripHtml } from "./utils/helper.js";
import { encodeNKeys } from "./utils/nkeys.js";
import { pool } from "./pool.js";
const KIND_FORM = 30168;
const KIND_MY_FORMS_LIST = 14083;
export class FormstrSDK {
    constructor() {
        // Serialises saveToMyForms writes per user so concurrent calls don't
        // race on the read-modify-write of the replaceable kind-14083 list.
        this.myFormsWriteQueue = new Map();
    }
    /** Fetch a form via NIP-101 naddr */
    //Discouraged use, will completely move to NKeys once app migrates.
    fetchFormWithViewKey(naddr, viewKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const nkeys = encodeNKeys({ viewKey });
            return yield this.fetchForm(naddr, nkeys);
        });
    }
    attachSubmitListener(form, signer, callbacks) {
        const formEl = document.getElementById(`form-${form.id}`);
        if (!formEl)
            return;
        formEl.addEventListener("submit", (e) => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            e.preventDefault(); // prevent page reload
            // Collect form values
            const formData = new FormData(formEl);
            const values = {};
            formData.forEach((v, k) => (values[k] = v));
            try {
                const result = yield this.submit(form, values, signer);
                (_a = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onSuccess) === null || _a === void 0 ? void 0 : _a.call(callbacks, {
                    event: result,
                    relays: form.relays,
                });
            }
            catch (err) {
                (_b = callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError) === null || _b === void 0 ? void 0 : _b.call(callbacks, err);
            }
        }));
    }
    fetchForm(naddr, nkeys) {
        return __awaiter(this, void 0, void 0, function* () {
            const rawForm = yield fetchFormTemplate(naddr, nkeys);
            if (!rawForm)
                return this.normalizeForm([["name", "Form Not Found"]]);
            return this.normalizeForm(rawForm);
        });
    }
    /** Normalize raw NIP-101 form tags into JS object */
    normalizeForm(raw) {
        var _a, _b;
        const idTag = raw.find((t) => t[0] === "d");
        const nameTag = raw.find((t) => t[0] === "name");
        const settingsTag = raw.find((t) => t[0] === "settings");
        const relaysTag = raw.filter((t) => t[0] === "relay");
        const relays = (relaysTag === null || relaysTag === void 0 ? void 0 : relaysTag.map((r) => r[1])) || [];
        const pubkey = ((_a = raw.find((t) => t[0] === "pubkey")) === null || _a === void 0 ? void 0 : _a[1]) || "";
        const formSettings = settingsTag
            ? JSON.parse(settingsTag[1])
            : {};
        const fields = {};
        const fieldOrder = [];
        raw
            .filter((t) => t[0] === "field")
            .forEach((t) => {
            const [_, fieldId, type, label, optionsStr, configStr] = t;
            fields[fieldId] = {
                id: fieldId,
                type,
                labelHtml: label,
                options: optionsStr
                    ? JSON.parse(optionsStr).map((o) => ({
                        id: o[0],
                        labelHtml: stripHtml(o[1]),
                        config: o[2] ? JSON.parse(o[2]) : undefined,
                    }))
                    : undefined,
                config: configStr ? JSON.parse(configStr) : {},
            };
            fieldOrder.push(fieldId);
        });
        const blocks = [];
        // Intro block (optional)
        if (nameTag || formSettings.description) {
            blocks.push({
                type: "intro",
                title: stripHtml(nameTag === null || nameTag === void 0 ? void 0 : nameTag[1]),
                description: stripHtml(formSettings.description),
            });
        }
        // Section blocks
        if ((_b = formSettings.sections) === null || _b === void 0 ? void 0 : _b.length) {
            blocks.push(...[...formSettings.sections]
                .sort((a, b) => a.order - b.order)
                .map((s) => ({
                type: "section",
                id: s.id,
                title: s.title,
                description: s.description,
                questionIds: s.questionIds,
                order: s.order,
            })));
        }
        else {
            // Fallback: single implicit section
            blocks.push({
                type: "section",
                id: "default",
                title: undefined,
                description: undefined,
                questionIds: fieldOrder,
                order: 0,
            });
        }
        return {
            id: (idTag === null || idTag === void 0 ? void 0 : idTag[1]) || "",
            blocks,
            name: stripHtml(nameTag === null || nameTag === void 0 ? void 0 : nameTag[1]),
            fields,
            fieldOrder,
            settings: formSettings,
            relays,
            pubkey,
        };
    }
    /** Render HTML form with submit wired using FormData */
    renderHtml(form) {
        var _a;
        const renderField = (field) => {
            var _a, _b;
            if (field.type === "text") {
                return `
        <label>${field.labelHtml}</label>
        <input type="text" name="${field.id}" />
      `;
            }
            if (field.type === "option" && field.options) {
                return `
        <div class="option-group">
          <div class="option-label">${field.labelHtml}</div>
          ${field.options
                    .map((opt) => `
              <label>
                <input type="radio" name="${field.id}" value="${opt.id}" />
                ${opt.labelHtml}
              </label>
            `)
                    .join("")}
        </div>
      `;
            }
            if (field.type === "label") {
                return `<p>${field.labelHtml}</p>`;
            }
            if (field.type === "grid" && field.options) {
                const gridOptions = field.options;
                const isCheckbox = field.config.allowMultiplePerRow;
                const inputType = isCheckbox ? "checkbox" : "radio";
                return `
        <div class="grid-question">
          <div class="grid-label">${field.labelHtml}</div>
          <table class="grid-table">
            <thead>
              <tr>
                <th></th>
                ${(_a = gridOptions.columns) === null || _a === void 0 ? void 0 : _a.map((col) => `<th>${col[1]}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${(_b = gridOptions.rows) === null || _b === void 0 ? void 0 : _b.map((row) => {
                    var _a;
                    return `
                <tr>
                  <td>${row[1]}</td>
                  ${(_a = gridOptions.columns) === null || _a === void 0 ? void 0 : _a.map((col) => `
                    <td>
                      <input
                        type="${inputType}"
                        name="${field.id}_${row[0]}"
                        value="${col[0]}"
                        ${inputType === "radio" ? "" : ""}
                      />
                    </td>
                  `).join("")}
                </tr>
              `;
                }).join("")}
            </tbody>
          </table>
        </div>
      `;
            }
            return "";
        };
        const renderBlock = (block) => {
            if (block.type === "intro") {
                return `
        <section class="form-section form-intro">
          ${block.title ? `<div class="form-name">${block.title}</div>` : ""}
          ${block.description
                    ? `<div class="form-description">${block.description}</div>`
                    : ""}
        </section>
      `;
            }
            if (block.type === "section") {
                return `
        <section class="form-section">
          ${block.title ? `<h2 class="section-title">${block.title}</h2>` : ""}
          ${block.description
                    ? `<div class="section-description">${block.description}</div>`
                    : ""}
          ${block.questionIds
                    .map((id) => renderField(form.fields[id]))
                    .join("\n")}
        </section>
      `;
            }
            return "";
        };
        const bodyHtml = (_a = form.blocks) === null || _a === void 0 ? void 0 : _a.map(renderBlock).join("\n");
        // Neutral wrapper
        form.html = {
            form: `
    <form id="form-${form.id}">
      <div class="form-body">
        ${bodyHtml}
      </div>
      <div id="submit-container">
        <button type="submit" id="form-submit-${form.id}">Submit</button>
      </div>
    </form>
  `,
        };
        return form;
    }
    /** Submit response back to relays, NIP-44 encrypted to the form's pubkey */
    submit(form, values, signer) {
        return __awaiter(this, void 0, void 0, function* () {
            const responseTags = Object.entries(values).map(([fieldId, value]) => {
                const field = form.fields[fieldId];
                if ((field === null || field === void 0 ? void 0 : field.type) === "grid") {
                    const jsonValue = typeof value === "string" ? value : JSON.stringify(value);
                    return ["response", fieldId, jsonValue, "{}"];
                }
                if (Array.isArray(value))
                    value = value.join(";");
                return ["response", fieldId, value, "{}"];
            });
            let content;
            let signerFn;
            if (signer) {
                // Identified submission: encrypt with the caller's NIP-44 key
                content = yield signer.nip44Encrypt(form.pubkey, JSON.stringify(responseTags));
                signerFn = (event) => signer.signEvent(event);
            }
            else {
                // Anonymous submission: ephemeral key used for both signing and encryption
                // so the form owner can derive the conversation key from the event pubkey
                const ephSk = generateSecretKey();
                const conversationKey = nip44.v2.utils.getConversationKey(ephSk, form.pubkey);
                content = nip44.v2.encrypt(JSON.stringify(responseTags), conversationKey);
                signerFn = (event) => Promise.resolve(finalizeEvent(event, ephSk));
            }
            const event = {
                kind: 1069,
                content,
                tags: [["a", `30168:${form.pubkey}:${form.id}`]],
                created_at: Math.floor(Date.now() / 1000),
            };
            const signed = yield signerFn(event);
            yield Promise.allSettled(pool.publish(form.relays, signed));
            return signed;
        });
    }
    /**
     * Publish a new form (kind 30168) using an ephemeral keypair.
     * If `options.encrypt` is true, fields are NIP-44 encrypted into `content`
     * and only the `d` tag is included (no `t` tag). Otherwise a public form
     * is created with fields in tags and `["t", "public"]`.
     * If `options.signer` is provided, the ephemeral keys are saved to the
     * user's encrypted MyForms list (kind 14083).
     */
    createForm(name, fields, options = {}) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const rawFields = fields.map(formFieldToTag);
            const relays = ((_a = options.relays) === null || _a === void 0 ? void 0 : _a.length) ? options.relays : getDefaultRelays();
            const signingKey = generateSecretKey();
            const signingKeyHex = bytesToHex(signingKey);
            const formPubkey = getPublicKey(signingKey);
            const formId = makeRandomId(6);
            let tags;
            let content;
            let viewKeyHex;
            if (options.encrypt !== false) {
                const viewKey = generateSecretKey();
                viewKeyHex = bytesToHex(viewKey);
                const conversationKey = nip44.v2.utils.getConversationKey(signingKey, getPublicKey(viewKey));
                content = nip44.v2.encrypt(JSON.stringify([["name", name], ...rawFields]), conversationKey);
                tags = [
                    ["d", formId],
                    ...relays.map((r) => ["relay", r]),
                ];
            }
            else {
                content = "";
                tags = [
                    ["d", formId],
                    ["name", name],
                    ...rawFields,
                    ["t", "public"],
                    ...relays.map((r) => ["relay", r]),
                ];
            }
            const event = {
                kind: KIND_FORM,
                created_at: Math.floor(Date.now() / 1000),
                tags,
                content,
            };
            const signed = finalizeEvent(event, signingKey);
            const formRelays = { accepted: [], rejected: [] };
            yield Promise.allSettled(pool.publish(relays, signed).map((p, i) => p
                .then(() => formRelays.accepted.push(relays[i]))
                .catch(() => formRelays.rejected.push(relays[i]))));
            const naddr = nip19.naddrEncode({
                pubkey: formPubkey,
                identifier: formId,
                relays: formRelays.accepted.length ? formRelays.accepted : relays,
                kind: KIND_FORM,
            });
            let myFormsEvent;
            let myFormsRelays;
            if (options.signer) {
                const secretData = viewKeyHex ? `${signingKeyHex}:${viewKeyHex}` : signingKeyHex;
                try {
                    ({ event: myFormsEvent, relays: myFormsRelays } = yield this.saveToMyForms(formPubkey, secretData, formId, relays, options.signer));
                }
                catch (_b) {
                    myFormsRelays = { accepted: [], rejected: relays };
                }
            }
            return Object.assign(Object.assign({ naddr,
                signingKeyHex }, (viewKeyHex && { viewKeyHex })), { formEvent: signed, formRelays,
                myFormsEvent,
                myFormsRelays });
        });
    }
    /**
     * Save ephemeral form keys to the user's encrypted MyForms list (kind 14083).
     * Uses the same NIP-44 encrypted tag format as the nostr-forms app:
     * `["f", "formPubkey:formId", relay, "secretKey"]`
     *
     * Calls are serialised per user pubkey so concurrent invocations (e.g. two
     * rapid /form inserts) never race on the read-modify-write cycle.
     */
    saveToMyForms(formAuthorPub, formAuthorSecretHex, formId, relays, signer) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const userPub = yield signer.getPublicKey();
            // Chain this write onto any in-flight write for the same user.
            // The .catch keeps a failed write from permanently blocking the queue.
            const prev = (_a = this.myFormsWriteQueue.get(userPub)) !== null && _a !== void 0 ? _a : Promise.resolve();
            const next = prev.then(() => this._writeToMyForms(userPub, formAuthorPub, formAuthorSecretHex, formId, relays, signer));
            this.myFormsWriteQueue.set(userPub, next.catch(() => { }));
            return next;
        });
    }
    _writeToMyForms(userPub, formAuthorPub, formAuthorSecretHex, formId, relays, signer) {
        return __awaiter(this, void 0, void 0, function* () {
            const targetRelays = relays.length ? relays : getDefaultRelays();
            // Always re-fetch inside the queue so we read the result of the previous
            // write, not a stale snapshot from before it was published.
            const existing = yield pool.get(targetRelays, {
                kinds: [KIND_MY_FORMS_LIST],
                authors: [userPub],
            });
            let forms = [];
            if (existing) {
                try {
                    const decrypted = yield signer.nip44Decrypt(userPub, existing.content);
                    forms = JSON.parse(decrypted);
                }
                catch (_a) {
                    forms = [];
                }
            }
            const key = `${formAuthorPub}:${formId}`;
            if (!forms.some((f) => f[1] === key)) {
                forms.push(["f", key, targetRelays[0], formAuthorSecretHex]);
            }
            const encrypted = yield signer.nip44Encrypt(userPub, JSON.stringify(forms));
            const listEvent = yield signer.signEvent({
                kind: KIND_MY_FORMS_LIST,
                created_at: Math.floor(Date.now() / 1000),
                tags: [],
                content: encrypted,
            });
            const publishResults = yield Promise.allSettled(pool.publish(targetRelays, listEvent));
            const relayResult = { accepted: [], rejected: [] };
            publishResults.forEach((r, i) => {
                if (r.status === "fulfilled")
                    relayResult.accepted.push(targetRelays[i]);
                else
                    relayResult.rejected.push(targetRelays[i]);
            });
            return { event: listEvent, relays: relayResult };
        });
    }
    /**
     * Fetch the user's saved forms from their encrypted MyForms list (kind 14083).
     * Returns a summary for each form — name, field count, and a ready-to-use naddr.
     * Forms whose kind-30168 event cannot be found on any relay are silently skipped.
     */
    fetchMyForms(signer, relays) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const userPub = yield signer.getPublicKey();
            const targetRelays = (relays === null || relays === void 0 ? void 0 : relays.length) ? relays : getDefaultRelays();
            const listEvent = yield pool.get(targetRelays, {
                kinds: [KIND_MY_FORMS_LIST],
                authors: [userPub],
            });
            if (!listEvent)
                return [];
            let entries;
            try {
                const decrypted = yield signer.nip44Decrypt(userPub, listEvent.content);
                entries = JSON.parse(decrypted);
            }
            catch (_b) {
                return [];
            }
            // Batch-fetch all the kind-30168 form events in one query
            const dTags = entries.map((f) => f[1].split(":")[1]).filter(Boolean);
            const pubkeys = entries.map((f) => f[1].split(":")[0]).filter(Boolean);
            const formEvents = yield pool.querySync(targetRelays, {
                kinds: [KIND_FORM],
                "#d": dTags,
                authors: pubkeys,
            });
            const summaries = [];
            for (const entry of entries) {
                const [, formData, relay, secretData] = entry;
                const [formPubkey, formId] = formData.split(":");
                if (!formPubkey || !formId)
                    continue;
                const event = formEvents.find((e) => e.pubkey === formPubkey && e.tags.some((t) => t[0] === "d" && t[1] === formId));
                if (!event)
                    continue;
                const name = ((_a = event.tags.find((t) => t[0] === "name")) === null || _a === void 0 ? void 0 : _a[1]) || "Untitled form";
                const fieldCount = event.tags.filter((t) => t[0] === "field").length;
                const eventRelays = event.tags
                    .filter((t) => t[0] === "relay")
                    .map((t) => t[1]);
                const naddr = nip19.naddrEncode({
                    pubkey: formPubkey,
                    identifier: formId,
                    relays: eventRelays.length ? eventRelays : [relay],
                    kind: KIND_FORM,
                });
                // secretData may be "signingKey" or "signingKey:viewKey" depending on whether
                // the form was saved by the nostr-forms app (which appends the viewKey).
                const [secretKey, viewKey] = (secretData !== null && secretData !== void 0 ? secretData : "").split(":");
                const keyObj = {};
                if (secretKey)
                    keyObj.secretKey = secretKey;
                if (viewKey)
                    keyObj.viewKey = viewKey;
                const nkeys = Object.keys(keyObj).length > 0 ? encodeNKeys(keyObj) : undefined;
                summaries.push({ naddr, formId, formPubkey, name, fieldCount, relay, nkeys });
            }
            return summaries;
        });
    }
}
const RENDER_ELEMENT_TO_PRIMITIVE = {
    shortText: "text",
    paragraph: "text",
    date: "text",
    time: "text",
    datetime: "text",
    signature: "text",
    fileUpload: "text",
    number: "number",
    label: "label",
    radioButton: "option",
    checkboxes: "option",
    dropdown: "option",
    multipleChoiceGrid: "grid",
    checkboxGrid: "grid",
};
function formFieldToTag(field) {
    var _a, _b, _c;
    const id = makeRandomId(8);
    const primitive = (_a = RENDER_ELEMENT_TO_PRIMITIVE[field.type]) !== null && _a !== void 0 ? _a : "text";
    const optionsJson = ((_b = field.options) === null || _b === void 0 ? void 0 : _b.length)
        ? JSON.stringify(field.options.map((o) => [makeRandomId(4), o]))
        : "[]";
    const config = JSON.stringify({ renderElement: field.type, required: (_c = field.required) !== null && _c !== void 0 ? _c : false });
    return ["field", id, primitive, field.label, optionsJson, config];
}
function makeRandomId(length) {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}
export function createEphemeralSigner() {
    const sk = generateSecretKey();
    const pk = getPublicKey(sk);
    return (event) => __awaiter(this, void 0, void 0, function* () {
        return finalizeEvent(Object.assign({}, event), sk);
    });
}
